const MULTIMEDIA_TYPES = [
  'image',
  'document',
  'audio',
  'video',
];

const regexes = [
  {
    pattern: /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/,
    replacement: '<a class="message__url" href="$&" rel="noopener noreferrer" target="_blank">$&</a>',
    isUrl: true,
  },
  {
    pattern: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
    replacement: '<a class="message__url" href="mailto:$&" rel="noopener noreferrer" target="_blank">$&</a>',
  },
  {
    pattern: /^\*(.*?)\*$/,
    replacement: '<b>$1</b>',
  },
  {
    pattern: /^_(.*?)_$/,
    replacement: '<i>$1</i>',
  },
  {
    pattern: /^~(.*?)~$/,
    replacement: '<span class="message__strikethrough">$1</span>',
  },
  {
    pattern: /^`{1,3}(.*?)`{1,3}$/,
    replacement: '<span class="message__monospace">$1</span>',
  },
];

export default {
  directives: {
    focuseModal: {
      inserted(el) {
        el.children[1].children[0].focus();
      },
    },
  },
  computed: {
    isHistory() {
      return this.$route.path === '/history';
    },
  },
  methods: {
    globalFormatRelativeDateTime(dt, forceTime = false, showToday = false) {
      let format;
      const today = this.$moment().local().startOf('day');
      const local = this.$moment(dt).local().startOf('day');
      const isFromToday = today.isSame(local, 'day');
      const isWithinLastWeek = today.diff(local, 'days') < 7;

      if (isFromToday) {
        if (showToday) {
          return 'Today';
        }

        format = 'h:mm A';
      } else if (isWithinLastWeek) {
        format = forceTime ? 'dddd h:mm A' : 'dddd';
      } else {
        format = forceTime ? 'DD/MM/YYYY h:mm A' : 'DD/MM/YYYY';
      }

      return this.$moment(dt).local().format(format);
    },
    globalFormatDatetime(dt) {
      return this.$moment(dt).local().format('DD/MM/YYYY h:mm A');
    },
    globalFormatTime(dt) {
      return this.$moment(dt).local().format('h:mm A');
    },
    globalFromNow(dt) {
      const today = this.$moment().local();
      const local = this.$moment(dt).local();
      const duration = this.$moment.duration(today.diff(local));

      const minutesDiff = Math.floor(duration.asMinutes());

      if (minutesDiff < 60) {
        return `${minutesDiff}m`;
      }
      const hoursDiff = Math.floor(duration.asHours());

      if (hoursDiff < 24) {
        return `${hoursDiff}h`;
      }
      const daysDiff = Math.floor(duration.asDays());

      if (daysDiff < 100) {
        return `${daysDiff}d`;
      }
      return '+99d';
    },
    actualTimeDifference(time) {
      const today = this.$moment().local();
      const timeLocal = this.$moment(time).local();
      return this.$moment.duration(today.diff(timeLocal));
    },
    buildMessage(clientId, channelId, campaignId, payload) {
      const required = {
        clientId,
        channelId,
        campaignId,
        user: payload.user ?? {},
        type: payload.type ?? 'text',
        tempId: payload.tempId ?? null,
        typeUser: payload.typeUser ?? 'User',
      };

      if (payload.type === 'text') {
        return {
          ...required,
          text: payload.text,
        };
      }

      if (payload.type === 'tText') {
        return {
          ...required,
          text: payload.text,
          template: payload.template,
        };
      }

      if (MULTIMEDIA_TYPES.includes(payload.type)) {
        if (payload.link) {
          return {
            ...required,
            link: payload.link,
          };
        }

        return {
          ...required,
          multimedia: payload.multimedia,
        };
      }

      if (payload.type === 'location') {
        const { lat, lng, name } = payload.location;

        return {
          ...required,
          location: {
            lat,
            lng,
            name,
          },
        };
      }

      return {};
    },
    uuidv4() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        // eslint-disable-next-line no-bitwise
        const r = Math.random() * 16 | 0;
        // eslint-disable-next-line no-bitwise
        const v = c === 'x' ? r : (r & 0x3);

        return v.toString(16);
      });
    },
    getGoogleMapsUrl(lat, lng) {
      const center = `center=${lat},${lng}`;
      const baseUrl = process.env.VUE_APP_GOOGLE_MAPS_BASE_URL;

      return `${baseUrl}?api=1&map_action=map&${center}`;
    },
    buildNotificationBody(message) {
      let body;

      if (message) {
        switch (message.type) {
          case 'tText':
          case 'text':
            body = message.text;
            break;
          case 'tImage':
          case 'image':
            body = 'Image';
            break;
          case 'audio':
          case 'voice':
            body = 'Audio';
            break;
          case 'video':
            body = 'Video';
            break;
          case 'location': {
            const loc = message.location.name ?? '';

            body = loc.length > 0 ? loc : 'Location';
            break;
          }
          case 'document': {
            const filename = message.multimedia.filename ?? '';

            body = filename.length > 0 ? filename : 'Document';
            break;
          }
          case 'transfer': {
            const { transfer = {} } = message;
            if (transfer.to && transfer.from) {
              body = this.$t('chat.transfer', {
                to: transfer.to.name,
                from: transfer.from.name,
              }).replace(/(<([^>]+)>)/ig, '');
            } else {
              body = 'Transfer';
            }
            break;
          }
          case 'statusChange': {
            const statusId = message.statusChange.to ?? 2;

            body = statusId > 0
              ? `${this.$t('chat.conversation')} ${this.translateStatus(statusId)}`
              : 'Status Change';
            break;
          }
          default:
            body = 'You have a new message.';
            break;
        }
      } else {
        body = 'You have a new message.';
      }

      return body;
    },
    translateStatus(id) {
      switch (id) {
        case 1:
          return this.$t('conversation-status.no-assign');
        case 2:
          return this.$t('conversation-status.assigned');
        case 3:
          return this.$t('conversation-status.process');
        case 4:
          return this.$t('conversation-status.resolved');
        case 5:
          return this.$t('conversation-status.cancelled');
        case 6:
          return this.$t('conversation-status.out-of-time');
        case 7:
          return this.$t('conversation-status.disconnected-agents');
        case 8:
          return 'IVR';
        default:
          return '';
      }
    },
    buildMultimediaMessage(message) {
      const {
        type, multimedia, conversation, id,
      } = message;
      return {
        data: [{
          id,
          multimedia,
        }],
        type,
        id: conversation.client,
      };
    },
    addHTMLTags(str) {
      const cleanStr = this.escapeHTML(str);

      return cleanStr.split(/\r\n|\n/).map((line) => line.split(' ').map((word) => {
        // eslint-disable-next-line no-restricted-syntax
        for (const regex of regexes) {
          const pattern = new RegExp(regex.pattern);

          if (pattern.test(word)) {
            if (regex.isUrl ?? false) {
              const prependProtocol = !word.startsWith('http');
              const replaced = prependProtocol ? `https://${word}` : word;

              return replaced.replace(regex.pattern, regex.replacement);
            }

            return word.replace(regex.pattern, regex.replacement);
          }
        }

        return word;
      }).join(' ')).join('\n');
    },
    escapeHTML(str) {
      return str?.replace(/[&<>'"]/g,
        (tag) => ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          "'": '&#39;',
          '"': '&quot;',
        }[tag]));
    },
  },
};
